import { useMemo } from "react";

import { DateTime } from "luxon";
import {
  CalendarDaysIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  CurrencyPoundIcon,
  FlagIcon,
  RocketLaunchIcon,
  TagIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import Card from "../shared/Card/Card";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import { ILead } from "../../models/Lead";
import { IVehicle } from "../../models/Vehicle";

type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>;

interface TimelineEvent {
  id: string;
  label: string;
  date: string | null | undefined;
  icon: IconType;
  colour: string;
}

const eventColour = (done: boolean) =>
  done
    ? "bg-primary-100 text-primary-700 ring-primary-200"
    : "bg-gray-100 text-gray-400 ring-gray-200";

interface VehicleTimelineProps {
  vehicle: IVehicle;
  lead: ILead | null | undefined;
}

// Vehicle-centric analogue of `StockTimeline`. The lifecycle is deliberately
// shorter than a part's — we only own the vehicle from first contact to
// export — but the visual grammar (pulsing current step, durations between
// milestones, em-dash for pending) is identical so operators flipping
// between pages stay oriented.
export default function VehicleTimeline({ vehicle, lead }: VehicleTimelineProps) {
  const events = useMemo<TimelineEvent[]>(() => {
    return [
      {
        id: "created",
        label: "Vehicle record created",
        date: vehicle.created,
        icon: TagIcon,
        colour: eventColour(!!vehicle.created),
      },
      {
        id: "leadCompleted",
        label: "Lead completed",
        date: lead?.completedOn ?? null,
        icon: FlagIcon,
        colour: eventColour(!!lead?.completedOn),
      },
      {
        id: "paymentConfirmed",
        label: "Payment confirmed",
        date: lead?.paymentConfirmedOn ?? vehicle.paymentConfirmedOn ?? null,
        icon: CurrencyPoundIcon,
        colour: eventColour(
          !!(lead?.paymentConfirmedOn ?? vehicle.paymentConfirmedOn)
        ),
      },
      {
        id: "bookedCollection",
        label: "Collection booked",
        date: vehicle.bookedCollectionOn,
        icon: CalendarDaysIcon,
        colour: eventColour(!!vehicle.bookedCollectionOn),
      },
      {
        id: "scheduledCollection",
        label: "Collection scheduled",
        date: vehicle.scheduledCollectionOn,
        icon: RocketLaunchIcon,
        colour: eventColour(!!vehicle.scheduledCollectionOn),
      },
      {
        id: "collected",
        label: "Collected",
        date: vehicle.collectedOn,
        icon: TruckIcon,
        colour: eventColour(!!vehicle.collectedOn),
      },
      {
        id: "checkedIn",
        label: "Checked in",
        // There's no single `checkedInOn` on IVehicle yet — `checkInId` is
        // the closest signal we have that operators have started the
        // check-in. We still show the step as pending/done based on that ID
        // so the timeline reads correctly even before the backend adds a
        // dedicated date field.
        date: vehicle.checkInId ? vehicle.completedOn ?? null : null,
        icon: ClipboardDocumentCheckIcon,
        colour: eventColour(!!vehicle.checkInId),
      },
      {
        id: "exported",
        label: "Exported",
        date: vehicle.exportedOn,
        icon: CheckBadgeIcon,
        colour: eventColour(!!vehicle.exportedOn),
      },
    ];
  }, [vehicle, lead]);

  const firstPendingId = useMemo(() => {
    const pending = events.find((e) => !e.date);
    return pending?.id;
  }, [events]);

  const durations = useMemo(() => {
    const byId: Record<string, { text: string; fromLabel: string } | null> = {};
    let prev: { date: DateTime; label: string } | null = null;
    for (const event of events) {
      if (!event.date) {
        byId[event.id] = null;
        continue;
      }
      const current = DateTime.fromISO(event.date);
      if (!current.isValid) {
        byId[event.id] = null;
        continue;
      }
      if (prev) {
        byId[event.id] = {
          text: formatGap(current.diff(prev.date).as("milliseconds")),
          fromLabel: prev.label,
        };
      } else {
        byId[event.id] = null;
      }
      prev = { date: current, label: event.label };
    }
    return byId;
  }, [events]);

  return (
    <Card title="Vehicle Lifecycle" bodyClassName="p-6">
      <ol className="relative border-l border-gray-200 ml-3">
        {events.map((event, idx) => {
          const isLast = idx === events.length - 1;
          const done = !!event.date;
          const isCurrent = event.id === firstPendingId;
          const Icon = done ? CheckCircleIcon : event.icon;

          return (
            <li
              key={event.id}
              className={classNames("ml-6", isLast ? "" : "pb-6")}
            >
              <span
                className={classNames(
                  "absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white",
                  event.colour,
                  isCurrent ? "animate-pulse" : ""
                )}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1">
                <p
                  className={classNames(
                    "text-sm font-medium",
                    done ? "text-gray-900" : "text-gray-400"
                  )}
                >
                  {event.label}
                </p>
                <p
                  className={classNames(
                    "text-xs",
                    done ? "text-gray-600" : "text-gray-400"
                  )}
                >
                  {formatDateTime(event.date, DateTime.DATETIME_SHORT, "Pending")}
                </p>
              </div>
              {durations[event.id] && (
                <p className="mt-0.5 text-[11px] text-gray-400">
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-gray-500">
                    +{durations[event.id]!.text}
                  </span>{" "}
                  <span className="text-gray-400">
                    after {durations[event.id]!.fromLabel}
                  </span>
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

function formatGap(ms: number): string {
  if (!isFinite(ms) || ms <= 0) return "same moment";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "min" : "mins"}`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days} ${days === 1 ? "day" : "days"}`;
  const weeks = Math.round(days / 7);
  if (weeks < 9) return `${weeks} ${weeks === 1 ? "week" : "weeks"}`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} ${months === 1 ? "month" : "months"}`;
  const years = Math.round(days / 365);
  return `${years} ${years === 1 ? "year" : "years"}`;
}
